(() => {
  'use strict';
  if (customElements.get('cansweet-receipt-date')) return;

  const key = 'Requested receipt date / Date de réception souhaitée';
  const controls = new Set();
  const state = { value: null, saved: null, pending: null, error: false, touched: false };
  const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const today = () => iso(new Date());
  const defaultDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return iso(date);
  };
  const valid = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
    const [year, month, day] = value.split('-').map(Number);
    return iso(new Date(year, month - 1, day, 12)) === value && value >= today();
  };
  const refresh = () => controls.forEach((control) => control.render());

  async function save() {
    if (!valid(state.value)) { refresh(); return false; }
    if (state.pending) return state.pending;
    if (state.saved === state.value && !state.error) { refresh(); return true; }
    state.error = false;
    state.pending = (async () => {
      // Serialize changes: a slower reply must never overwrite a newer selection.
      while (state.saved !== state.value && valid(state.value)) {
        const value = state.value;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/update.js`, {
            method: 'POST', credentials: 'same-origin', signal: controller.signal,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { [key]: value } }),
          });
          if (!response.ok) throw new Error('Date save failed');
          const cart = await response.json();
          if (cart.attributes?.[key] !== value) throw new Error('Date not acknowledged');
          state.saved = value;
        } finally {
          clearTimeout(timeout);
        }
      }
      return valid(state.value) && state.saved === state.value;
    })().catch(() => { state.error = true; return false; }).finally(() => {
      state.pending = null;
      refresh();
    });
    refresh();
    return state.pending;
  }

  class ReceiptDate extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.hint = this.querySelector('[data-date-hint]');
      this.status = this.querySelector('[data-date-status]');
      this.retry = this.querySelector('[data-date-retry]');
      this.formId = this.input.getAttribute('form');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      if (state.value === null) {
        state.saved = this.dataset.storedValue || '';
        state.touched = Boolean(state.saved);
        state.value = state.saved || defaultDate();
      }
      controls.add(this);
      this.input.addEventListener('input', () => this.select(false), options);
      this.input.addEventListener('change', () => this.select(true), options);
      this.input.addEventListener('focus', () => { state.touched = true; refresh(); }, options);
      this.retry.addEventListener('click', () => save(), options);
      document.addEventListener('submit', (event) => this.beforeCheckout(event), { ...options, capture: true });
      refresh();
      if (this.dataset.hasItems === 'true' && valid(state.value)) save();
    }

    disconnectedCallback() {
      controls.delete(this);
      this.abort?.abort();
    }

    select(persist) {
      state.value = this.input.value;
      state.touched = true;
      state.error = false;
      refresh();
      if (persist && valid(state.value)) save();
    }

    render() {
      if (this.input.value !== state.value) this.input.value = state.value;
      this.input.min = today();
      const isValid = valid(state.value);
      this.input.setCustomValidity(isValid ? '' : this.dataset.invalid);
      this.input.setAttribute('aria-invalid', String(!isValid));
      this.hint.hidden = !isValid || !state.touched;
      const text = !isValid ? this.dataset.invalid : state.error ? this.dataset.error : state.pending ? this.dataset.saving : '';
      this.status.textContent = text;
      this.status.hidden = !text;
      this.retry.hidden = !state.error;
      this.setAttribute('aria-busy', String(Boolean(state.pending)));
      // Accelerated buttons skip the native cart form; expose them only after save.
      const scope = this.closest('.cart__blocks');
      scope?.querySelectorAll('.additional-checkout-buttons').forEach((buttons) => {
        const blocked = !isValid || state.error || Boolean(state.pending) || state.saved !== state.value;
        buttons.inert = blocked;
        buttons.classList.toggle('cs-receipt-date-pending', blocked);
      });
    }

    async beforeCheckout(event) {
      if (event.target.id !== this.formId || event.submitter?.name !== 'checkout' || this.allowSubmit) return;
      if (!valid(state.value)) {
        event.preventDefault();
        this.input.reportValidity();
        this.input.focus();
        return;
      }
      if (state.saved === state.value && !state.pending && !state.error) return;
      event.preventDefault();
      if (this.submitting) return;
      this.submitting = true;
      const form = event.target, submitter = event.submitter;
      if (await save()) {
        this.allowSubmit = true;
        try { form.requestSubmit(submitter); } finally { this.allowSubmit = false; }
      }
      this.submitting = false;
    }
  }
  customElements.define('cansweet-receipt-date', ReceiptDate);
})();

